import { getApiUrl } from './api.js'
import { cropPhotoFile } from './photoAlbumCropUpload.js'

const GUEST_PHOTO_MANAGER_PATCH = Symbol.for('eInvitation.photoAlbumGuestUploadedPhotos.v2')

function normalizeAlbumId(value) {
  return String(value || '')
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[_\s]+/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase()
}

function getAlbumIdFromLocation() {
  const match = window.location.pathname.match(/\/e-photo-album\/([^/?#]+)/i)
  return normalizeAlbumId(match ? decodeURIComponent(match[1]) : '')
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

function remainingLabel(deadline, allowed) {
  if (!allowed || !deadline) return '24-hour editing period ended'
  const remaining = Math.max(0, new Date(deadline).getTime() - Date.now())
  const hours = Math.floor(remaining / (60 * 60 * 1000))
  const minutes = Math.max(1, Math.ceil((remaining % (60 * 60 * 1000)) / (60 * 1000)))
  return hours > 0
    ? `${hours}h ${minutes}m left to replace`
    : `${minutes}m left to replace`
}

function formatUploadedAt(value) {
  try {
    return new Intl.DateTimeFormat('en-PH', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(value))
  } catch {
    return ''
  }
}

async function readJson(response) {
  const payload = await response.json().catch(() => ({}))
  if (!response.ok) {
    const error = new Error(payload.message || 'Request failed.')
    error.code = payload.code
    throw error
  }
  return payload
}

function createManagerModal(albumId) {
  const overlay = document.createElement('div')
  overlay.className = 'epa-my-uploads-overlay'
  overlay.setAttribute('role', 'dialog')
  overlay.setAttribute('aria-modal', 'true')
  overlay.setAttribute('aria-label', 'My uploaded photos')
  overlay.innerHTML = `
    <section class="epa-my-uploads-dialog">
      <header class="epa-my-uploads-header">
        <div>
          <span>YOUR EVENT PHOTOS</span>
          <h2>My Uploaded Photos</h2>
          <p>Photos can be replaced and cropped within 24 hours of their original upload.</p>
        </div>
        <button type="button" class="epa-my-uploads-close" aria-label="Close uploaded photos">×</button>
      </header>
      <div class="epa-my-uploads-content" aria-live="polite">
        <div class="epa-my-uploads-loading">Loading your photos…</div>
      </div>
    </section>
  `

  const content = overlay.querySelector('.epa-my-uploads-content')
  const closeButton = overlay.querySelector('.epa-my-uploads-close')
  let objectUrls = []
  let closed = false

  function revokeObjectUrls() {
    objectUrls.forEach((url) => URL.revokeObjectURL(url))
    objectUrls = []
  }

  function close() {
    if (closed) return
    closed = true
    revokeObjectUrls()
    overlay.remove()
    document.body.classList.remove('epa-my-uploads-open')
    window.removeEventListener('keydown', handleKeyDown)
  }

  function handleKeyDown(event) {
    if (event.key === 'Escape' && !document.querySelector('.epa-crop-overlay')) close()
  }

  async function loadPreview(photo, image) {
    try {
      const response = await fetch(getApiUrl(photo.previewPath), {
        credentials: 'include',
        cache: 'no-store',
      })
      if (!response.ok) throw new Error('Unable to load photo preview.')
      const blob = await response.blob()
      const objectUrl = URL.createObjectURL(blob)
      objectUrls.push(objectUrl)
      if (!closed && image.isConnected) image.src = objectUrl
    } catch {
      image.closest('.epa-my-upload-card')?.classList.add('is-preview-error')
      image.alt = 'Photo preview unavailable'
    }
  }

  async function replacePhoto(photo, button) {
    const picker = document.createElement('input')
    picker.type = 'file'
    picker.accept = 'image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif'

    picker.addEventListener('change', async () => {
      const selected = picker.files?.[0]
      if (!selected) return

      button.disabled = true
      button.textContent = 'Opening crop…'
      try {
        const cropped = await cropPhotoFile(selected)
        if (!cropped) {
          button.disabled = false
          button.textContent = 'Replace & Crop'
          return
        }

        button.textContent = 'Replacing…'
        const formData = new FormData()
        formData.append('photo', cropped, cropped.name)
        const response = await fetch(getApiUrl(
          `/photo-albums/${encodeURIComponent(albumId)}/my-uploads/${encodeURIComponent(photo.submissionId)}/${encodeURIComponent(photo.id)}/replace`
        ), {
          method: 'POST',
          body: formData,
          credentials: 'include',
        })
        await readJson(response)
        window.dispatchEvent(new CustomEvent('photo-album-uploaded'))
        await loadPhotos()
      } catch (error) {
        window.alert(error.message || 'Unable to replace this photo.')
        button.disabled = false
        button.textContent = 'Replace & Crop'
      }
    }, { once: true })

    picker.click()
  }

  async function renderPhotos(photos) {
    revokeObjectUrls()

    if (!photos.length) {
      content.innerHTML = `
        <div class="epa-my-uploads-empty">
          <strong>No uploaded photos yet</strong>
          <p>Your successfully uploaded photos will appear here.</p>
        </div>
      `
      return
    }

    content.innerHTML = `
      <div class="epa-my-uploads-grid">
        ${photos.map((photo, index) => `
          <article class="epa-my-upload-card" data-photo-index="${index}">
            <div class="epa-my-upload-preview">
              <img alt="Your uploaded photo ${index + 1}" draggable="false" />
            </div>
            <div class="epa-my-upload-details">
              <strong>Photo ${index + 1}</strong>
              <span>Uploaded ${escapeHtml(formatUploadedAt(photo.uploadedAt))}</span>
              <p class="${photo.replaceAllowed ? '' : 'is-expired'}">
                ${escapeHtml(remainingLabel(photo.replaceDeadline, photo.replaceAllowed))}
              </p>
              ${photo.replaceAllowed ? '<button type="button" class="epa-my-upload-replace">Replace & Crop</button>' : '<button type="button" class="epa-my-upload-replace" disabled>Editing Closed</button>'}
            </div>
          </article>
        `).join('')}
      </div>
    `

    photos.forEach((photo, index) => {
      const card = content.querySelector(`[data-photo-index="${index}"]`)
      const image = card?.querySelector('img')
      const replaceButton = card?.querySelector('.epa-my-upload-replace')
      if (image) loadPreview(photo, image)
      if (replaceButton && photo.replaceAllowed) {
        replaceButton.addEventListener('click', () => replacePhoto(photo, replaceButton))
      }
    })
  }

  async function loadPhotos() {
    content.innerHTML = '<div class="epa-my-uploads-loading">Loading your photos…</div>'
    try {
      const response = await fetch(getApiUrl(
        `/photo-albums/${encodeURIComponent(albumId)}/my-uploads`
      ), {
        credentials: 'include',
        cache: 'no-store',
      })
      const payload = await readJson(response)
      await renderPhotos(Array.isArray(payload.photos) ? payload.photos : [])
    } catch (error) {
      content.innerHTML = `
        <div class="epa-my-uploads-error">
          <strong>Unable to load your photos</strong>
          <p>${escapeHtml(error.message)}</p>
          <button type="button">Try Again</button>
        </div>
      `
      content.querySelector('button')?.addEventListener('click', loadPhotos)
    }
  }

  closeButton.addEventListener('click', close)
  overlay.addEventListener('click', (event) => {
    if (event.target === overlay && !document.querySelector('.epa-crop-overlay')) close()
  })
  window.addEventListener('keydown', handleKeyDown)
  document.body.appendChild(overlay)
  document.body.classList.add('epa-my-uploads-open')
  closeButton.focus()
  loadPhotos()

  return { close }
}

function decorateQuotaPanel(status, albumId, root = document) {
  root.querySelectorAll?.('.epa-guest-upload-status').forEach((panel) => {
    panel.classList.add('epa-guest-upload-status-simple')
    const message = panel.querySelector(':scope > p')
    const remaining = Math.max(0, Number(status.remainingCount) || 0)
    const nextMessage = `You may upload ${remaining} more photo${remaining === 1 ? '' : 's'}.`
    if (message && message.textContent !== nextMessage) message.textContent = nextMessage

    let button = panel.querySelector('.epa-view-my-uploads')
    const shouldShow = Boolean(status.nameLocked && Number(status.uploadedCount) > 0)
    if (!shouldShow) {
      button?.remove()
      return
    }

    if (!button) {
      button = document.createElement('button')
      button.type = 'button'
      button.className = 'epa-view-my-uploads'
      button.textContent = 'View My Uploaded Photos'
      if (message) panel.insertBefore(button, message)
      else panel.prepend(button)
    }

    button.onclick = () => createManagerModal(albumId)
  })
}

export function installPhotoAlbumGuestUploadedPhotos() {
  if (typeof document === 'undefined' || globalThis[GUEST_PHOTO_MANAGER_PATCH]) return
  const albumId = getAlbumIdFromLocation()
  if (!albumId) return

  let status = {
    nameLocked: false,
    uploadedCount: 0,
    remainingCount: 5,
  }

  const apply = (root = document) => decorateQuotaPanel(status, albumId, root)

  window.addEventListener('photo-album-guest-status', (event) => {
    status = { ...status, ...(event.detail || {}) }
    apply(document)
  })

  const start = () => {
    apply(document)
    const observer = new MutationObserver((mutations) => {
      let shouldApplyDocument = false
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue
          apply(node)
          if (node.matches?.('.epa-guest-upload-status') || node.querySelector?.('.epa-guest-upload-status')) {
            shouldApplyDocument = true
          }
        }
      }
      if (shouldApplyDocument) apply(document)
    })
    observer.observe(document.documentElement, { childList: true, subtree: true })
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true })
  } else {
    start()
  }

  globalThis[GUEST_PHOTO_MANAGER_PATCH] = true
}
